import mergeObjects from 'lodash/merge';
import { DateTime, Duration } from 'luxon';
import * as Yup from 'yup';

import { getTenantUnits } from '@core/configuration';
import { ProjectStatus } from '@core/types/api/ProjectStatus/ProjectStatus';
import { EmptyTenantDefaultMargins, TenantDefaultMargins } from '@core/types/tenantDefaultMargins';

import type { Company } from '../types/api/Company/Company';
import type { Contact } from '../types/api/Contact/Contact';
import { Cost, NewCost } from '../types/api/Cost/Cost';
import { Currency } from '../types/api/Currency/Currency';
import type { CustomFieldDefinition } from '../types/api/CustomFieldDefinition/CustomFieldDefinition';
import Enums from '../types/api/Enums';
import { Forecast } from '../types/api/Forecast/Forecast';
import { Project, ProjectWireFormat } from '../types/api/Project/Project';
import type { ProjectCompetitorWireFormat } from '../types/api/ProjectCompetitor/ProjectCompetitor';
import { NewProjectPlant, type ProjectPlant } from '../types/api/ProjectPlant/ProjectPlant';
import { NewProjectProduct, type ProjectProduct } from '../types/api/ProjectProduct/ProjectProduct';
import { Segment } from '../types/api/Segment/Segment';
import { PartialExcept } from '../types/app';
import { CalculateTargetMarginCurrency } from '../utils/costCalculations';
import { CurrencyString, NullableCostString, NullableCurrencyString } from '../utils/currency';
import { hasStringValue, hasValue } from '../utils/equality';
import { getProductCategoriesInProject } from '../utils/forecastDistribution';
import { resolveCurrencyCode } from '../utils/format';
import {
  FormikAddress,
  FormikCurrency,
  FormikDeliveryCosts,
  FormikNullableCurrency,
} from '../utils/formikHelpers';
import { ParseCurrency, ParseNullableCurrency } from '../utils/formParsingHelpers';
import { NIL_UUID, isValidUUID, randomUUID } from '../utils/uuid';
import {
  CalculateWithDeliveryTotalPrice,
  ProductDeliveryTypeIsValid,
} from '../utils/withDeliveryPriceCalculations';
import {
  TestForUndefined,
  YupAddress,
  YupAddressSometimesCityRequired,
  YupCustomFieldDefinition,
  type YupCustomFieldDefinitionType,
  YupForecasts,
  type YupFormikForecasts,
  YupNullableBoolean,
  YupNullableCurrency,
  YupNullableDecimal,
  YupNullableDuration,
  YupNullableEnum,
  YupNullableLocalDate,
  YupNullableNumber,
  YupNullablePercentage,
  YupNullableReference,
  YupNullableString,
  YupReference,
  type YupSchemaNullableReferenceType,
  type YupSchemaReferenceType,
  YupSometimesNullableCurrency,
  YupSometimesNullableLocalDate,
  YupSometimesNullableNumber,
  YupSometimesNullableReference,
  YupString,
  nonNegativeStringNumber,
} from '../utils/yupHelpers';
import { type CompanyFormikType, CompanySchema } from './company';
import {
  type ContactFormikType,
  NewBaseContactSchema,
  type RequiredContactFieldsInfo,
} from './contact';
import { NewDeliveryCostOrNullFromFormik } from './plant';
import {
  CompetitorLossFormikType,
  competitorLossFormikSchemaFields,
  competitorLossFromFormik,
  competitorLossToFormik,
} from './projectOrQuoteCompetitor';
import {
  FormikProductReference,
  MainProjectOrQuoteProductsFormikSchema,
  type ProjectOrQuoteProductEditableFieldsFormikType,
  type ProjectOrQuoteProductFormikType,
  buildAdditionalTruckingInfoFormik,
} from './projectOrQuoteProduct';

export type CompanyContactType = {
  id: string;
  isNewCompany: boolean;
  isNewContact: boolean;
  company: YupSchemaReferenceType | CompanyFormikType;
  contact: YupSchemaNullableReferenceType | ContactFormikType;
};

/**
 * CompanyContactToFormik converts a Company and a maybe-null Contact into a formik-friendly
 * object.
 */
export const CompanyContactToFormik = ({
  company,
  contact,
  usesDispatchCustomer,
}: {
  company: Company;
  contact: Contact | null;
  usesDispatchCustomer: boolean;
}): CompanyContactType => ({
  id: randomUUID(),
  isNewCompany: false,
  isNewContact: false,
  company: {
    id: company.id,
    option: {
      value: company.id,
      label: company.name,
      sublabels: company.lookupSublabels(usesDispatchCustomer),
    },
  },
  contact: !hasValue(contact?.id)
    ? null
    : {
        id: contact?.id ?? null,
        option: !hasValue(contact) ? null : { value: contact.id, label: contact.fullName() },
      },
});

export type RequiredProjectFieldsInfo = {
  estimatedStartDate: boolean;
  estimatedEndDate: boolean;
  bidDate: boolean;
  expirationDate: boolean;
  city: boolean;
  address: boolean;
  contractors: boolean;
  revenueOverride: boolean;
  readyMixQuantityOverride: boolean;
  segment: boolean;
  taxCode: boolean;
  products: boolean;
  config: boolean;
  productDeliveryType: boolean;
  lossDetails: boolean;
  expirationDateShouldBeAfterStartDate: boolean;
};

export type ProjectBulkEditFormikType = Pick<ProjectWireFormat, 'bidDate' | 'expirationDate'> & {
  user: YupSchemaNullableReferenceType;
  projectStatus: YupSchemaNullableReferenceType;
  segment: YupSchemaNullableReferenceType;
};

export type ProjectStatusBulkEditRequestPayload = Pick<ProjectStatus, 'id' | 'priority'>;

export type ProjectsBulkEditRequestPayload = {
  id: string;
  projectStatus?: PartialExcept<Project['projectStatus'], 'id'>;
  bidDate?: Project['bidDate'];
  expirationDate?: Project['expirationDate'];
  user?: PartialExcept<Project['user'], 'id'>;
  segment?: Pick<Segment, 'id'>;
};

export type ProductsByPlantFormikType = {
  plant: YupSchemaReferenceType;
  plantDistance: string | null;
  plantDistanceUnit: Enums.Unit | null;
  plantDriveTime: string | null;
  products: ProjectOrQuoteProductFormikType[];
  autoSelected?: boolean;
};

export type ProductsByPlantEditableFieldsFormikType = Omit<
  ProductsByPlantFormikType,
  'products'
> & {
  products: ProjectOrQuoteProductEditableFieldsFormikType[];
};

const ProductsByPlantSchemaFormik: Yup.SchemaOf<ProductsByPlantEditableFieldsFormikType> =
  Yup.object({
    plant: YupReference('Plant'),
    plantDistance: YupNullableString('Location distance'),
    plantDistanceUnit: YupNullableEnum(Enums.Unit, 'Location distance unit'),
    plantDriveTime: YupNullableString('Drive time'),
    autoSelected: Yup.boolean().optional(),
    products: MainProjectOrQuoteProductsFormikSchema,
  });

export const ProjectBulkEditSchemaFormik: Yup.SchemaOf<ProjectBulkEditFormikType> =
  Yup.object().shape({
    bidDate: YupNullableLocalDate('Bid date'),
    expirationDate: YupNullableLocalDate('Expiration date').test(
      'expiration-after-bid',
      'Expiration date must be after bid date',
      (expirationDate, context) => {
        const { bidDate } = context.parent;
        if (!bidDate || !expirationDate) {
          return true; // Skip validation if either date is not set
        }
        return new Date(expirationDate) > new Date(bidDate);
      },
    ),
    user: YupNullableReference('User'),
    projectStatus: YupNullableReference('Status'),
    segment: YupNullableReference('Segment'),
  });

export type ProjectFormikType = Omit<
  ProjectWireFormat,
  | 'plant'
  | 'plantDistance'
  | 'plantDistanceUnit'
  | 'plantDriveTime'
  | 'products'
  | 'companies'
  | 'totalCosts'
  | 'estimatedVolume'
  | 'readyMixQuantity'
  | 'readyMixQuantityUnit'
  | 'aggregateQuantity'
  | 'aggregateQuantityUnit'
  | 'asphaltQuantity'
  | 'asphaltQuantityUnit'
  | 'revenue'
  | 'margin'
  | 'customFields'
  | 'winningCompany'
  | 'user'
  | 'projectStatus'
  | 'forecasts'
  | 'taxCode'
  | 'segment'
  | 'competitor'
  | 'projectConfig'
  | 'lossReason'
  | 'createdAt'
  | 'createdBy'
  | 'lastStatusChange'
  | 'lastStatusChangeBy'
  | 'plants'
> &
  CompetitorLossFormikType & {
    user: YupSchemaReferenceType;
    productsByPlants: ProductsByPlantFormikType[];
    contractors: CompanyContactType[];
    otherCompanies: CompanyContactType[];
    winningCompany: {
      company: YupSchemaNullableReferenceType;
    };
    projectStatus: YupSchemaReferenceType & {
      systemBehavior: Enums.ProjectStatusSystemBehavior | null;
    };
    customFields: YupCustomFieldDefinitionType[];
    // projectID & plantID are purposefully omitted since the server does not look at it.
    forecasts: YupFormikForecasts;
    taxCode: YupSchemaNullableReferenceType;
    segment: YupSchemaNullableReferenceType;
    projectConfig: YupSchemaNullableReferenceType;
    // This flag is required to know if the plant distance has changed
    // so that we can recalculate the products' delivery costs.
    hasDistanceChanged: boolean;
    isPubliclyFunded: boolean | null;
    forecastDistributionMethod: 'monthly' | 'quarterly';
  };

const sharedProjectSchemaFields = {
  name: YupString('Name'),
  projectNumber: YupNullableString('Project number'),
  projectStatus: YupReference('Status'),
  isPubliclyFunded: YupNullableBoolean('Publicly Funded'),
  readyMixQuantityOverride: YupNullableNumber({
    label: 'Volume',
    integer: true,
    positive: true,
  }),
  readyMixQuantityOverrideUnit: Yup.mixed<Enums.Unit>().nullable().defined(),
  aggregateQuantityOverride: YupNullableNumber({
    label: 'Aggregate Volume',
    integer: true,
    positive: true,
  }),
  aggregateQuantityOverrideUnit: Yup.mixed<Enums.Unit>().nullable().defined(),
  asphaltQuantityOverride: YupNullableNumber({
    label: 'Asphalt Volume',
    integer: true,
    positive: true,
  }),
  asphaltQuantityOverrideUnit: Yup.mixed<Enums.Unit>().nullable().defined(),
  totalCostsOverride: YupNullableCurrency('Total cost'),
  revenueOverride: YupNullableCurrency('Revenue'),
  confidence: YupNullablePercentage('Confidence', 1)
    .test(nonNegativeStringNumber)
    .test('max-100', 'Must be less than or equal to 100', (value) => Number(value) <= 100),
  terms: YupNullableString('Terms/T&C'),
  notes: YupNullableString('Notes (Internal Only)'),
  projectJobNumber: YupNullableString('Project Job Number'),

  // When dates are cleared, the value is replaced with empty string
  estimatedStartDate: YupNullableLocalDate('Start date'),
  estimatedEndDate: YupNullableLocalDate('End date'),
  bidDate: YupNullableLocalDate('Bid date'),
  expirationDate: YupNullableLocalDate('Expiration date'),

  address: YupAddress('Address'),
  user: YupReference('User'),

  productsByPlants: Yup.array().of(
    Yup.object({
      plant: YupReference('Location'),
      plantDistance: YupNullableDecimal('Location distance')
        .transform((v) => v?.replaceAll(',', ''))
        .test(nonNegativeStringNumber),
      plantDistanceUnit: Yup.mixed<Enums.Unit>().nullable().defined(),
      plantDriveTime: YupNullableDuration('Drive time'),
      autoSelected: Yup.boolean().optional(),
      products: MainProjectOrQuoteProductsFormikSchema,
    }),
  ),

  winningCompany: Yup.object()
    .shape({
      company: YupNullableReference('Company'),
    })
    .nullable()
    .transform((c: { company: YupSchemaNullableReferenceType } | null) =>
      c?.company?.id === null ? null : c,
    ),

  owner: YupNullableString('Owner'),

  forecasts: YupForecasts('Forecast'),

  taxCode: YupNullableReference('Tax code'),
  segment: YupNullableReference('Segment'),
  projectConfig: YupNullableReference('Quote config'),
  forecastDistributionMethod: Yup.mixed<'monthly' | 'quarterly'>()
    .oneOf(['monthly', 'quarterly'])
    .default('monthly'),
};

export const NewYupProjectCompany = (requiredContactFields: RequiredContactFieldsInfo) =>
  Yup.array().of(
    Yup.object({
      isNewCompany: Yup.boolean().required(),
      isNewContact: Yup.boolean().required(),

      company: Yup.mixed().when('isNewCompany', {
        is: true,
        then: CompanySchema,
        otherwise: YupReference('Company'),
      }),

      contact: Yup.mixed().when(['isNewCompany', 'isNewContact', 'company'], {
        is: (isNewCompany: boolean, isNewContact: boolean, company: YupSchemaReferenceType) => {
          // Only apply contact validation if company is selected and contact is new
          const hasCompany = company?.id !== null && company?.id !== undefined;
          return (isNewCompany || isNewContact) && hasCompany;
        },
        then: NewBaseContactSchema(requiredContactFields),
        otherwise: YupNullableReference('Contact'),
      }),
    }),
  );

export const YupProjectCompany = NewYupProjectCompany({
  fullContactInfo: false,
  requiresEmail: false,
});

type ProjectSchemaBaseType = Omit<
  ProjectFormikType,
  | 'id'
  | 'companies'
  | 'contractors'
  | 'otherCompanies'
  | 'hasDistanceChanged'
  | 'customFields'
  | 'projectStatus'
  | 'archiveQuotes'
  | 'displayOnly'
> & {
  productsByPlants: ProductsByPlantEditableFieldsFormikType[];
};

/** This schema will be used for yup validation */
export const ProjectSchemaFormik: Yup.SchemaOf<ProjectSchemaBaseType> = Yup.object()
  .shape({
    ...sharedProjectSchemaFields,
    ...competitorLossFormikSchemaFields,
    contractors: YupProjectCompany,
    otherCompanies: YupProjectCompany,
  })
  .test(TestForUndefined('ProjectSchemaFormik'));

// On the wire the competitor data is a single nested ProjectCompetitor object,
// not the flattened formik fields. Drop the formik competitor fields and add
// the nested wire field below.
type ProjectSchemaWireBaseType = Omit<ProjectSchemaBaseType, keyof CompetitorLossFormikType> & {
  competitor: ProjectCompetitorWireFormat | null;
};

/** This schema will be used for yup validation */
export const ProjectSchemaWire: Yup.SchemaOf<ProjectSchemaWireBaseType> = Yup.object().shape({
  ...sharedProjectSchemaFields,
  // Pass the nested wire competitor object through untouched (see QuoteSchemaWire).
  competitor: Yup.mixed<ProjectCompetitorWireFormat>().nullable(),
  companies: YupProjectCompany,
}) as Yup.SchemaOf<ProjectSchemaWireBaseType>;

export const NewProjectSchemaFormik: (
  requiredFields: RequiredProjectFieldsInfo,
  requiredContactFields: RequiredContactFieldsInfo,
) => Yup.SchemaOf<ProjectSchemaBaseType> = (requiredFields, requiredContactFields) =>
  Yup.object()
    .shape({
      ...sharedProjectSchemaFields,
      ...competitorLossFormikSchemaFields,
      contractors: NewYupProjectCompany(requiredContactFields).min(
        requiredFields.contractors ? 1 : 0,
      ),
      otherCompanies: NewYupProjectCompany(requiredContactFields),
      projectStatus: YupReference('Status'),
      lossReason: YupNullableReference('Loss Reason').test(
        'loss-reason-required-when-lost',
        'Loss reason is required when project is Lost',
        function testLossReasonRequired(value) {
          if (!requiredFields.lossDetails) {
            return true;
          }
          const projectStatus = (
            this.parent as { projectStatus?: { systemBehavior?: string | null } }
          ).projectStatus;
          if (projectStatus?.systemBehavior === Enums.ProjectStatusSystemBehavior.Lost) {
            return hasValue(value?.id);
          }
          return true;
        },
      ),
      competitor: YupNullableReference('Competitor').test(
        'competitor-required-when-lost',
        'Competitor is required when project is Lost',
        function testCompetitorRequired(value) {
          if (!requiredFields.lossDetails) {
            return true;
          }
          const projectStatus = (
            this.parent as { projectStatus?: { systemBehavior?: string | null } }
          ).projectStatus;
          if (projectStatus?.systemBehavior === Enums.ProjectStatusSystemBehavior.Lost) {
            return hasValue(value?.id);
          }
          return true;
        },
      ),
      estimatedStartDate: YupSometimesNullableLocalDate(
        'Start date',
        requiredFields.estimatedStartDate,
      ),
      estimatedEndDate: YupSometimesNullableLocalDate(
        'End date',
        requiredFields.estimatedEndDate,
      ).test('after-start-date', 'End date must be after start date', (endDate, context) => {
        const startDate = context.resolve(Yup.ref('estimatedStartDate')) as string;
        if (!endDate || !startDate) return true;

        return DateTime.fromISO(endDate) >= DateTime.fromISO(startDate);
      }),
      bidDate: YupSometimesNullableLocalDate('Bid date', requiredFields.bidDate),
      expirationDate: YupSometimesNullableLocalDate(
        'Expiration date',
        requiredFields.expirationDate,
      )
        .test(
          'expiration-after-bid',
          'Expiration date must be after bid date',
          (expirationDate, context) => {
            const { bidDate } = context.parent;
            if (!bidDate || !expirationDate) {
              return true; // Skip validation if either date is not set
            }
            return new Date(expirationDate) > new Date(bidDate);
          },
        )
        .test(
          'expiration-after-start-date',
          'Expiration date must be on or after start date',
          (expirationDate, context) => {
            if (!requiredFields.expirationDateShouldBeAfterStartDate) return true;

            const startDate = context.resolve(Yup.ref('estimatedStartDate')) as string;
            if (!expirationDate || !startDate) return true;

            return DateTime.fromISO(expirationDate) >= DateTime.fromISO(startDate);
          },
        ),
      address: YupAddressSometimesCityRequired(
        'Address',
        requiredFields.city,
        requiredFields.address,
      ),
      revenueOverride: YupSometimesNullableCurrency(
        'Revenue override',
        requiredFields.revenueOverride,
      ),
      readyMixQuantityOverride: YupSometimesNullableNumber(
        {
          label: 'Volume',
          integer: true,
          positive: true,
        },
        requiredFields.readyMixQuantityOverride,
      ),
      projectConfig: YupSometimesNullableReference('Configuration template', requiredFields.config),
      segment: YupSometimesNullableReference('Segment', requiredFields.segment),
      taxCode: YupSometimesNullableReference('Tax Code', requiredFields.taxCode),
      customFields: Yup.array().of(YupCustomFieldDefinition()),
      productsByPlants: Yup.array()
        .of(ProductsByPlantSchemaFormik)
        .test('required-products', 'At least one product is required', (value) => {
          if (!requiredFields.products) {
            return true;
          }
          const productsByPlants = value;

          if (!productsByPlants || productsByPlants.length === 0) {
            return false;
          }

          const totalValidProductCount = productsByPlants.reduce((acc: number, plant) => {
            const validProducts = plant.products?.filter((p) => p.product && p.product.id) || [];

            return acc + validProducts.length;
          }, 0);

          return totalValidProductCount > 0;
        })
        .test(
          'required-product-delivery-type',
          'Delivery type is required for all aggregate and asphalt products',
          (value) =>
            requiredFields.productDeliveryType ? ProductDeliveryTypeIsValid(value) : true,
        ) as unknown as Yup.SchemaOf<ProductsByPlantEditableFieldsFormikType[]>,
    })
    .test(TestForUndefined('ProjectSchemaFormik'));

export const RecalculateSuggestedPrice = (projectProduct: ProjectProduct): Currency | null => {
  const p = NewProjectProduct({
    ...projectProduct,
    deliveryCosts: {
      ...projectProduct.deliveryCosts,
      totalDeliveryCost: projectProduct.deliveryCosts.calculateTotalDeliveryCost() ?? Cost.zero(),
    },
  });
  const price = p.calculateSuggestedPrice();
  return price;
};

export const FormatSuggestedPrice = (amount: Currency | null, unit: Enums.Unit): string =>
  NullableCostString({
    cost: NewCost({
      amount: amount ?? Currency.zero(),
      unit,
    }),
  });

/** Returns the form data for the provided Project, including the specified custom fields. */
export const FormikProject = (
  projectData: Partial<Project> | undefined,
  customFieldDefinitions: CustomFieldDefinition[],
  usesDispatchCustomer: boolean,
  isMoneySwitchOn: boolean,
  defaultMargins: TenantDefaultMargins = EmptyTenantDefaultMargins,
  marginConfiguration: Enums.MarginConfiguration = Enums.MarginConfiguration.Percentage,
  /** Puts every company in `contractors` and leaves `otherCompanies` empty, for surfaces
   * presenting a single unified company list instead of a category-split one. */
  mergeAllCompanies = false,
): ProjectFormikType => {
  const partialProject = projectData ?? {};

  // Extract unique product categories from project products, ignoring unspecified/Other
  const productCategories = new Set<Enums.ProductCategory>();
  (partialProject.products ?? []).forEach((product) => {
    const category = product.product?.category;
    if (hasValue(category) && category !== Enums.ProductCategory.Other) {
      productCategories.add(category);
    }
  });

  // If no products yet, default to Ready Mix for backward compatibility
  if (productCategories.size === 0) {
    productCategories.add(Enums.ProductCategory.ReadyMix);
  }

  // Generate forecasts for each product category
  const projectForecasts = Array.from(productCategories)
    .flatMap((category) =>
      Forecast.fillForecasts({
        kind: Enums.ForecastKind.Project,
        currentForecasts: partialProject.forecasts ?? [],
        productCategory: category,
        tenantUnits: getTenantUnits(),
      }),
    )
    .map<ProjectFormikType['forecasts'][number]>((pf) => ({
      id: pf.id,
      kind: pf.kind,
      value: pf.value,
      unit: pf.unit,
      productCategory: pf.productCategory,
      intervalLength: pf.intervalLength.toISO() ?? '',
      intervalStart: pf.intervalStart.toISO() ?? '',
    }));

  const getProducts = (products: ProjectProduct[]): ProjectOrQuoteProductFormikType[] => {
    return products.map((p) => {
      const productReference = FormikProductReference(p.product);

      // Money display strings use the currency of the product's plant,
      // falling back to the tenant default.
      const currencyCode = resolveCurrencyCode(p.product.plant.currencyCode);

      const taxRate = parseFloat(p.taxRate ?? '0') / 100;
      const haulRate = p.haulRate ?? Currency.zero();
      const withDeliverySubtotalPrice = haulRate.add(p.price);
      const withDeliveryTotalPrice = CalculateWithDeliveryTotalPrice({
        unitPrice: p.price,
        haulRate,
        taxRate,
        haulTaxable: p.haulTaxable,
      });

      return {
        product: productReference,
        quantity: p.quantity,
        externalName: p.externalName,
        usage: p.usage,
        price: FormikCurrency(p.price),
        originalPrice: FormikCurrency(p.price),
        deliveryCosts: FormikDeliveryCosts(p.deliveryCosts, currencyCode),
        isMoneySwitchOn,
        displayOnly: {
          // Product-specific
          listPrice: NullableCurrencyString({ cur: p.product.listPrice, currencyCode }),
          targetMargin: p.product.targetMarginDisplay(
            marginConfiguration ?? Enums.MarginConfiguration.Percentage,
            defaultMargins,
          ),
          targetMarginPercentage: p.product.targetMarginDisplay(
            Enums.MarginConfiguration.Percentage,
            defaultMargins,
          ),
          targetMarginCurrency: CalculateTargetMarginCurrency({
            product: p.product,
            totalCost: p.costPerUnit().toUnit(),
            defaultMargins,
            currencyCode,
          }),

          suggestedPrice: NullableCurrencyString({
            cur: p.calculateSuggestedPrice(
              marginConfiguration ?? Enums.MarginConfiguration.Percentage,
              defaultMargins,
            ),
            currencyCode,
          }),
          actualMarginPercentage: p.marginAsPercentage(),
          unitCost: CurrencyString({ cur: p.costPerUnit(), currencyCode }),
          actualMarginCurrency: p.marginAsFormattedCurrency(currencyCode),
          marginOverMaterialsCurrency: p.marginOverMaterialAsFormattedCurrency(currencyCode),
          marginOverMaterialsPercentage: p.marginOverMaterialAsPercentage(),
        },

        // Shared fields with Quote Products that always remain empty.
        class: null,
        kind: Enums.QuoteProductKind.Primary,

        // Delivery Pricing-displayOnly fields
        withDeliveryDisplayOnly: {
          withDeliverySubtotalPrice: NullableCurrencyString({
            cur: withDeliverySubtotalPrice,
            currencyCode,
          }),
          withDeliveryTotalPrice: NullableCurrencyString({
            cur: withDeliveryTotalPrice,
            currencyCode,
          }),
          haulMargin: p.haulMarginAsPercentage(),
        },

        // Delivery Pricing-specific fields
        haulTaxable: p.haulTaxable,
        deliveryType: p.deliveryType ?? null,
        haulRate: FormikCurrency(p.haulRate ?? undefined),
        minimumHaulCharge: p.minimumHaulCharge,
        taxRate: p.taxRate !== null ? parseFloat(p.taxRate).toFixed(2) : null,
        truckingType: {
          id: p.truckingType?.id ?? null,
          option:
            p.truckingType === null
              ? null
              : {
                  value: p.truckingType.id,
                  label: p.truckingType.name,
                },
        },
        haulCost: FormikCurrency(p.haulCost ?? undefined),
        additionalTruckingInfos: (p.additionalTruckingInfos ?? []).map((info) =>
          buildAdditionalTruckingInfoFormik(info, p.price, currencyCode),
        ),
      };
    });
  };

  const getProductsByPlants = (projectPlants: ProjectPlant[]): ProductsByPlantFormikType[] => {
    return projectPlants.map((projectPlant: ProjectPlant): ProductsByPlantFormikType => {
      return {
        plant: {
          id: projectPlant.plant.id,
          option: {
            value: projectPlant.plant.id,
            label: projectPlant.plant.name,
          },
        },
        plantDistance: projectPlant.plantDistance ?? null,
        plantDistanceUnit: projectPlant.plantDistanceUnit ?? null,
        plantDriveTime: projectPlant.plantDriveTime?.toISO() ?? null,
        autoSelected: false,
        products: getProducts(
          (partialProject.products ?? []).filter(
            (p) => p.product.plant.id === projectPlant.plant.id,
          ),
        ),
      };
    });
  };

  return {
    id: partialProject.id ?? NIL_UUID,
    name: partialProject.name ?? '',
    projectNumber: partialProject.projectNumber ?? null,
    projectStatus: {
      id: isValidUUID(partialProject.projectStatus?.id) ? partialProject.projectStatus.id : null,
      option: !isValidUUID(partialProject.projectStatus?.id)
        ? null
        : {
            value: partialProject.projectStatus.id,
            label: partialProject.projectStatus.name,
          },
      systemBehavior: partialProject.projectStatus?.systemBehavior ?? null,
    },
    totalCostsOverride: partialProject.totalCostsOverride ?? null,
    readyMixQuantityOverride: partialProject.readyMixQuantityOverride ?? null,
    readyMixQuantityOverrideUnit: partialProject.readyMixQuantityOverrideUnit ?? null,
    aggregateQuantityOverride: partialProject.aggregateQuantityOverride ?? null,
    aggregateQuantityOverrideUnit: partialProject.aggregateQuantityOverrideUnit ?? null,
    asphaltQuantityOverride: partialProject.asphaltQuantityOverride ?? null,
    asphaltQuantityOverrideUnit: partialProject.asphaltQuantityOverrideUnit ?? null,
    revenueOverride: FormikNullableCurrency(partialProject.revenueOverride),
    estimatedStartDate: partialProject.estimatedStartDate?.toISO() ?? null,
    estimatedEndDate: partialProject.estimatedEndDate?.toISO() ?? null,
    bidDate: partialProject.bidDate?.toISO() ?? null,
    expirationDate: partialProject.expirationDate?.toISO() ?? null,
    confidence: hasValue(partialProject.confidence)
      ? String(parseFloat((parseFloat(partialProject.confidence) * 100).toFixed(3)))
      : null,
    terms: partialProject.terms ?? null,
    notes: partialProject.notes ?? null,
    owner: partialProject.owner ?? null,
    projectJobNumber: partialProject.projectJobNumber ?? null,
    address: FormikAddress(partialProject.address),
    productsByPlants: getProductsByPlants(partialProject.plants ?? []),
    user: {
      id: partialProject.user?.id ?? null,
      option:
        partialProject.user === undefined
          ? null
          : {
              value: partialProject.user.id,
              label: partialProject.user.fullName(),
            },
    },
    winningCompany: {
      company: {
        id: partialProject.winningCompany?.company?.id ?? null,
        option:
          partialProject.winningCompany?.company === undefined
            ? null
            : {
                value: partialProject.winningCompany.company.id,
                label: partialProject.winningCompany.company.name,
              },
      },
    },
    contractors: (partialProject.companies ?? [])
      .filter((pc) => mergeAllCompanies || pc.company.category === Enums.CompanyCategory.Contractor)
      .map((pc) =>
        CompanyContactToFormik({
          company: pc.company,
          contact: pc.contact,
          usesDispatchCustomer,
        }),
      ),
    otherCompanies: (partialProject.companies ?? [])
      .filter(
        (pc) => !mergeAllCompanies && pc.company.category !== Enums.CompanyCategory.Contractor,
      )
      .map((pc) =>
        CompanyContactToFormik({
          company: pc.company,
          contact: pc.contact,
          usesDispatchCustomer,
        }),
      ),

    customFields: customFieldDefinitions.map((cfd) => {
      const value = ((): string | null => {
        const cfValue =
          partialProject.customFields?.find((cf) => cf.definition.id === cfd.id)?.value ?? null;
        return cfValue === null ? null : String(cfValue);
      })();
      return {
        definition: cfd,
        value,
      };
    }),
    forecasts: projectForecasts,
    taxCode: {
      id: partialProject.taxCode?.id ?? null,
      option: !hasValue(partialProject.taxCode)
        ? null
        : {
            value: partialProject.taxCode.id,
            label: partialProject.taxCode.name,
            sublabels: [partialProject.taxCode.code],
          },
    },
    segment: {
      id: partialProject.segment?.id ?? null,
      option: !hasValue(partialProject.segment)
        ? null
        : {
            value: partialProject.segment.id,
            label: partialProject.segment.name,
          },
    },
    projectConfig: {
      id: partialProject.projectConfig?.id ?? null,
      option: !hasValue(partialProject.projectConfig)
        ? null
        : {
            value: partialProject.projectConfig.id,
            label: partialProject.projectConfig.name,
          },
    },
    ...competitorLossToFormik(partialProject.competitor),
    hasDistanceChanged: false,
    isPubliclyFunded: partialProject.isPubliclyFunded ?? null,
    forecastDistributionMethod: 'monthly',
  };
};

export const EmptyFormikProject = FormikProject(
  undefined,
  [],
  false,
  false,
  EmptyTenantDefaultMargins,
  undefined,
);

// TODO #2219 Need a NewProjectFromForm wrapper for this case
export const formikProjectToWire = (values: ProjectFormikType): Project => {
  // Only include forecasts for product categories that still have products in
  // the project. Without this, removing all products of a category leaves stale
  // forecast entries that get persisted to the backend on save.
  // When no products exist at all, skip filtering (project defaults to Mix forecasts).
  const validCategories = new Set<string>(getProductCategoriesInProject(values.productsByPlants));
  const hasProducts = validCategories.size > 0;
  const normalizedForecasts = values.forecasts
    .filter(
      (forecast) =>
        !hasProducts || !forecast.productCategory || validCategories.has(forecast.productCategory),
    )
    .map((forecast) => {
      if (forecast.id !== NIL_UUID && !hasStringValue(forecast.value)) {
        // Keep cleared existing forecasts so the server overwrites prior values.
        return { ...forecast, value: '0' };
      }
      return forecast;
    });

  const wireCompetitor = competitorLossFromFormik(values);

  const plants = values.productsByPlants.map((item) => {
    return NewProjectPlant({
      plant: { id: item.plant.id ?? undefined, name: item.plant.option?.label ?? '' },
      plantDistance: item.plantDistance ?? null,
      plantDistanceUnit: (item.plantDistanceUnit as Enums.Unit | null) ?? null,
      plantDriveTime: item.plantDriveTime ? Duration.fromISO(item.plantDriveTime) : null,
    });
  });

  const products = values.productsByPlants
    .flatMap((item) => item.products)
    .map((p) => ({
      ...p,
      // Strip commas from currency fields before wire conversion
      price: ParseCurrency(p.price),
      haulRate: ParseNullableCurrency(p.haulRate),
      haulCost: ParseNullableCurrency(p.haulCost),
      deliveryCosts: NewDeliveryCostOrNullFromFormik(p.deliveryCosts),
      truckingType: hasValue(p.truckingType?.id) ? p.truckingType : null,
      additionalTruckingInfos: p.additionalTruckingInfos?.map((info) => ({
        ...info,
        haulRate: ParseNullableCurrency(info.haulRate),
        haulCost: ParseNullableCurrency(info.haulCost),
        truckingType: hasValue(info.truckingType?.id) ? info.truckingType : null,
      })),
    }));

  const project = mergeObjects(Project.zero(), {
    ...values,
    forecasts: normalizedForecasts,
    // Strip commas from project-level currency fields
    revenueOverride: ParseNullableCurrency(values.revenueOverride),
    totalCostsOverride: ParseNullableCurrency(values.totalCostsOverride),
    plants,
    companies: values.contractors.concat(values.otherCompanies).map((c) => {
      const company = c.company as CompanyFormikType;
      const wireCompany = c.isNewCompany
        ? {
            ...company,
            marketSummaries: company.markets.map((m) => ({
              id: m.id,
              name: m.option?.label ?? '',
            })),
            dispatchCustomers: Object.values(company.dispatchCustomers)
              .filter((dc) => dc.id !== null)
              .map((dc) => ({
                ...dc,
                // Match CompanyDrawer wire mapping for integrated dispatch customers.
                externalId: null,
              })),
          }
        : c.company;

      if (c.isNewContact && c.contact?.id === NIL_UUID) {
        const contactData = c.contact as ContactFormikType;
        return {
          company: wireCompany,
          contact: {
            isArchived: false,
            firstName: contactData.firstName !== '' ? contactData.firstName : '',
            lastName:
              contactData.lastName !== null && contactData.lastName !== ''
                ? contactData.lastName
                : null,
            jobTitle:
              contactData.jobTitle !== null && contactData.jobTitle !== ''
                ? contactData.jobTitle
                : null,
            emailAddress:
              contactData.emailAddress !== null && contactData.emailAddress !== ''
                ? contactData.emailAddress
                : null,
            phoneNumber:
              contactData.phoneNumber !== null && contactData.phoneNumber !== ''
                ? contactData.phoneNumber
                : null,
          },
        };
      }

      return {
        company: wireCompany,
        contact: ((): { id: string } | null => {
          // Handle case where contact is stored as string ID (legacy format)
          if (typeof c.contact === 'string' && c.contact !== '' && c.contact !== NIL_UUID) {
            return { id: c.contact };
          }
          // Handle case where contact is stored as reference object
          if (hasValue(c.contact?.id) && c.contact?.id !== '') {
            return { id: c.contact?.id };
          }
          return null;
        })(),
      };
    }),
    confidence:
      values.confidence !== null && values.confidence !== ''
        ? (parseFloat(values.confidence) / 100).toFixed(3)
        : null,
    customFields: values.customFields
      .filter((cf) => cf.value !== null)
      .map((cf) => ({
        definition: {
          id: cf.definition.id,
        },
        value: cf.value !== null ? cf.value.trim() : null,
      })),
    products,
    competitor: wireCompetitor,
  });

  return new Project({ ...ProjectSchemaWire.cast(project) });
};
